import { useState } from 'react';
import { AlertCircle, Loader2, ShieldOff, UserPlus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { ApiError } from '@/lib/api';
import {
  formatInviteDate,
  personDisplayName,
  validateEmail,
} from './companyOnboarding';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import {
  PLATFORM_ROLE_OPTIONS,
  platformRoleLabel,
  useCreatePlatformAdmin,
  usePlatformAdmins,
  usePlatformMe,
  useRevokePlatformAdmin,
  useUpdatePlatformAdminRole,
  type PlatformAdmin,
  type PlatformRole,
} from './hooks/usePlatformPeopleQueries';

/**
 * `/platform-admin/admins` — кто управляет платформой (AK-572).
 *
 * Пункт меню вёл в 404, хотя CRUD на бэкенде готов целиком (аудит 12.08).
 *
 * Реестр и любые изменения — только для суперадмина; остальным сервер
 * отвечает 403, и экран говорит об этом словами. Показывать пустую таблицу
 * там, где на самом деле «не хватает прав», нельзя: человек решит, что
 * администраторов нет ни одного.
 *
 * Себя из списка не выключаем и роль себе не меняем — это делает сервер, но
 * и кнопок таких не рисуем: подсунуть самому себе понижение в один клик
 * означало бы остаться без единственного суперадмина.
 */
export function PlatformAdminAdminsPage() {
  const me = usePlatformMe();
  const isSuperadmin = me.data?.platform_role === 'superadmin';
  const admins = usePlatformAdmins(me.isSuccess && isSuperadmin);
  const create = useCreatePlatformAdmin();
  const updateRole = useUpdatePlatformAdminRole();
  const revoke = useRevokePlatformAdmin();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<PlatformRole>('support');
  const [formError, setFormError] = useState<string | null>(null);
  // Ошибки действий над СТРОКАМИ — отдельно от ошибок формы создания. Раньше
  // они писались в formError, а тот не только рисуется внутри карточки
  // «Назначить администратора», но и висит в `aria-invalid` на поле «Почта»:
  // сорвавшаяся смена роли у чужой строки внизу сообщала скринридеру, что
  // невалидна почта в форме вверху.
  const [rowError, setRowError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PlatformAdmin | null>(null);
  // Понижение суперадмина подтверждаем: селект — самый простой контрол для
  // промаха (колесо мыши над открытым списком, стрелки с клавиатуры), а цена
  // промаха — платформа без суперадмина.
  const [demoteTarget, setDemoteTarget] = useState<{
    admin: PlatformAdmin;
    next: PlatformRole;
  } | null>(null);

  const rows = admins.data ?? [];

  const submit = async () => {
    setNotice(null);
    const emailError = validateEmail(email);
    setFormError(emailError);
    if (emailError) return;
    try {
      const created = await create.mutateAsync({
        email,
        role,
        firstName,
        lastName,
      });
      setEmail('');
      setFirstName('');
      setLastName('');
      setNotice(
        `${created.email} — ${platformRoleLabel(created.platform_role)}. Если это новый человек, ему ушло письмо со входом.`,
      );
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.detail || 'Не удалось назначить роль'
          : 'Не удалось назначить роль',
      );
    }
  };

  const applyRole = async (a: PlatformAdmin, next: PlatformRole) => {
    setNotice(null);
    setRowError(null);
    setPendingUserId(a.user_id);
    try {
      await updateRole.mutateAsync({ userId: a.user_id, role: next });
      setDemoteTarget(null);
      setNotice(
        `${personDisplayName(a)} — теперь ${platformRoleLabel(next).toLowerCase()}.`,
      );
    } catch (err) {
      setRowError(
        err instanceof ApiError
          ? err.detail || 'Не удалось сменить роль'
          : 'Не удалось сменить роль',
      );
    } finally {
      setPendingUserId(null);
    }
  };

  /** Понижение суперадмина спрашиваем, остальные смены применяем сразу. */
  const requestRoleChange = (a: PlatformAdmin, next: PlatformRole) => {
    if (next === a.platform_role) return;
    if (a.platform_role === 'superadmin') {
      setRowError(null);
      setDemoteTarget({ admin: a, next });
      return;
    }
    void applyRole(a, next);
  };

  const revokeRole = async (a: PlatformAdmin) => {
    setNotice(null);
    setRowError(null);
    setPendingUserId(a.user_id);
    try {
      await revoke.mutateAsync({ userId: a.user_id });
      setRevokeTarget(null);
      setNotice(`${personDisplayName(a)} (${a.email}) больше не администратор платформы.`);
    } catch (err) {
      setRowError(
        err instanceof ApiError
          ? err.detail || 'Не удалось снять роль'
          : 'Не удалось снять роль',
      );
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <PlatformAdminLayout>
      <div className="flex h-full flex-col">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Админы</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Кто управляет платформой. Роль решает, что человек может делать в
            чужих компаниях — поддержка входит только на чтение.
          </p>
        </div>

        {me.isLoading && <Skeleton className="mt-4 h-24 w-full" />}

        {me.isError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center gap-3">
              <span className="flex-1">
                Не удалось узнать вашу роль — экран не может решить, что вам
                показывать.
              </span>
              <Button variant="outline" size="sm" onClick={() => void me.refetch()}>
                Повторить
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {me.isSuccess && !isSuperadmin && (
          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Реестр администраторов виден только суперадмину. Ваша роль —{' '}
              {platformRoleLabel(me.data?.platform_role)}.
            </AlertDescription>
          </Alert>
        )}

        {isSuperadmin && (
          <>
            <Card className="mt-4 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Назначить администратора
              </p>
              <form
                className="mt-3 flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit();
                }}
              >
                <div className="min-w-[220px] flex-1 space-y-1">
                  <Label htmlFor="admin-email">Почта</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="support@aikolxoz.com"
                    aria-invalid={!!formError}
                  />
                </div>
                <div className="min-w-[120px] flex-1 space-y-1">
                  <Label htmlFor="admin-first">Имя</Label>
                  <Input
                    id="admin-first"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Иван"
                  />
                </div>
                <div className="min-w-[120px] flex-1 space-y-1">
                  <Label htmlFor="admin-last">Фамилия</Label>
                  <Input
                    id="admin-last"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Иванов"
                  />
                </div>
                <div className="min-w-[200px] space-y-1">
                  <Label htmlFor="admin-role">Роль</Label>
                  <Select
                    value={role}
                    onValueChange={(v) => setRole(v as PlatformRole)}
                  >
                    <SelectTrigger id="admin-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORM_ROLE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                  Назначить
                </Button>
              </form>
              <p className="mt-2 text-xs text-muted-foreground">
                {
                  PLATFORM_ROLE_OPTIONS.find((o) => o.value === role)?.hint
                }
              </p>
              {formError && (
                <p role="alert" className="mt-2 text-xs text-destructive">
                  {formError}
                </p>
              )}
              {notice && (
                <Alert className="mt-3">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{notice}</AlertDescription>
                </Alert>
              )}
            </Card>

            <Card className="mt-4 min-h-0 flex-1 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Администраторы{admins.isSuccess ? ` — ${rows.length}` : ''}
              </p>

              {admins.isLoading && (
                <div className="mt-3 space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              )}

              {rowError && (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {rowError}
                </p>
              )}

              {admins.isError && (
                <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span className="flex-1">
                    Не удалось загрузить список администраторов
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void admins.refetch()}
                  >
                    Повторить
                  </Button>
                </div>
              )}

              <div className="mt-2 divide-y divide-border/60">
                {rows.map((a) => {
                  const isMe = a.user_id === me.data?.user_id;
                  return (
                    <div
                      key={a.user_id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3"
                    >
                      <div className="min-w-0 flex-1 basis-[240px]">
                        <p className="truncate text-sm font-medium text-foreground">
                          {personDisplayName(a)}
                          {isMe && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              это вы
                            </span>
                          )}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {a.email}
                        </p>
                      </div>

                      <div className="basis-[190px] text-xs text-muted-foreground">
                        назначил {a.granted_by_name ?? '—'}
                        {a.granted_at ? `, ${formatInviteDate(a.granted_at)}` : ''}
                      </div>

                      {!a.is_active && (
                        <Badge variant="destructive">Доступ закрыт</Badge>
                      )}

                      {isMe ? (
                        <Badge variant="accent-brand">
                          {platformRoleLabel(a.platform_role)}
                        </Badge>
                      ) : (
                        <div className="w-[200px]">
                          <Select
                            value={a.platform_role}
                            disabled={pendingUserId === a.user_id}
                            onValueChange={(v) =>
                              requestRoleChange(a, v as PlatformRole)
                            }
                          >
                            <SelectTrigger
                              aria-label={`Роль администратора ${a.email}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PLATFORM_ROLE_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {!isMe && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pendingUserId === a.user_id}
                          onClick={() => {
                            setRowError(null);
                            setRevokeTarget(a);
                          }}
                        >
                          <ShieldOff className="h-4 w-4" />
                          Снять роль
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              {admins.isSuccess && rows.length === 0 && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Администраторов пока нет — назначьте первого формой выше.
                </p>
              )}
            </Card>
            <ConfirmActionDialog
              open={revokeTarget !== null}
              title="Снять роль администратора платформы?"
              description={
                revokeTarget
                  ? `${personDisplayName(revokeTarget)} (${revokeTarget.email}) сейчас ${platformRoleLabel(revokeTarget.platform_role).toLowerCase()}. После снятия он потеряет доступ к админке платформы; сама учётная запись и работа в компаниях останутся.`
                  : ''
              }
              confirmLabel="Снять роль"
              destructive
              pending={revoke.isPending}
              error={rowError}
              onConfirm={() => {
                if (revokeTarget) void revokeRole(revokeTarget);
              }}
              onOpenChange={(open) => {
                if (!open) {
                  setRevokeTarget(null);
                  setRowError(null);
                }
              }}
            />

            <ConfirmActionDialog
              open={demoteTarget !== null}
              title="Понизить суперадмина?"
              description={
                demoteTarget
                  ? `${personDisplayName(demoteTarget.admin)} (${demoteTarget.admin.email}) перестанет быть суперадмином и станет «${platformRoleLabel(demoteTarget.next)}». Назначать администраторов он больше не сможет — убедитесь, что суперадмин на платформе остаётся не один.`
                  : ''
              }
              confirmLabel="Понизить"
              destructive
              pending={updateRole.isPending}
              error={rowError}
              onConfirm={() => {
                if (demoteTarget)
                  void applyRole(demoteTarget.admin, demoteTarget.next);
              }}
              onOpenChange={(open) => {
                if (!open) {
                  setDemoteTarget(null);
                  setRowError(null);
                }
              }}
            />
          </>
        )}
      </div>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminAdminsPage;
